import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';

interface Tenant {
  id: number;
  razao_social: string;
  status: string;
  current_plan?: { id: number; name: string; code?: string } | null;
}

interface Props {
  tenant: Tenant;
  onClose: () => void;
}

function formatCents(cents?: number) {
  if (cents == null) return '';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function ChangePlanModal({ tenant, onClose }: Props) {
  const qc = useQueryClient();
  const currentPlanId = tenant.current_plan?.id ?? null;
  const [planId, setPlanId] = useState<number | null>(currentPlanId);

  // Esc fecha
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const { data: plans, isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => apiClient.admin.listPlans().then(r => r.data as any[]),
  });

  const changeMut = useMutation({
    mutationFn: () => apiClient.admin.changePlan(tenant.id, planId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] });
      const name = plans?.find(p => p.id === planId)?.name ?? 'plano';
      toast.success(`Plano alterado para ${name}`);
      onClose();
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? 'Falha ao alterar plano';
      toast.error(msg);
    },
  });

  const selected = plans?.find(p => p.id === planId);
  const unchanged = planId === currentPlanId;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-bold">Alterar plano</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Tenant info */}
          <div className="bg-muted/30 rounded-lg p-3 text-sm">
            <div className="font-semibold">{tenant.razao_social}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Status: <span className="font-mono">{tenant.status}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Plano atual: <strong>{tenant.current_plan?.name ?? '—'}</strong>
            </div>
          </div>

          {/* Plan selector */}
          <div>
            <label className="text-sm font-medium block mb-1">Novo plano</label>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Carregando planos...</div>
            ) : (
              <select
                value={planId ?? ''}
                onChange={(e) => setPlanId(e.target.value ? Number(e.target.value) : null)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="" disabled>Selecione um plano</option>
                {plans?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.is_public === false ? ' (interno)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Selected plan summary */}
          {selected && (
            <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm space-y-0.5">
              <div className="font-semibold text-brand-700">{selected.name}</div>
              {selected.description && (
                <div className="text-xs text-muted-foreground">{selected.description}</div>
              )}
              <div className="text-xs text-muted-foreground">
                1ª TV: <strong>{formatCents(selected.first_tv_monthly_cents)}/mês</strong>
                {' · '}TV adicional: <strong>{formatCents(selected.price_per_tv_monthly_cents)}/mês</strong>
              </div>
              <div className="text-xs text-muted-foreground">
                Limite de TVs: <strong>{selected.max_tvs ?? 'ilimitado'}</strong>
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground">
            Troca apenas o plano vigente — não altera o status nem o ciclo de trial.
            Para ativar uma assinatura use "Ativar manualmente".
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-muted/10">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => changeMut.mutate()}
            disabled={changeMut.isPending || planId == null || unchanged}
          >
            {changeMut.isPending ? 'Salvando...' : 'Salvar plano'}
          </Button>
        </div>
      </div>
    </div>
  );
}
